import React, { useState, useMemo, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FiSearch, FiStar, FiBookOpen, FiPlus, FiEdit2, FiTrash2, FiSettings } from 'react-icons/fi';
import { CATEGORIES } from '../../data/researchStore';
import { apiGetAllResearch, apiDeleteResearch } from '../../api/researchApi';
import { useAuth } from '../../context/AuthContext';
import GoogleSignInGate from '../../components/GoogleSignInGate';
import ConfirmModal from '../../components/ConfirmModal';

export default function ResearchCatalogue() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isAdmin = user?.isAdmin;
  const [query, setQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [sortBy, setSortBy] = useState('relevance');
  const [dateFilter, setDateFilter] = useState('any');
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Sync category from URL query param
  useEffect(() => {
    const cat = searchParams.get('category');
    if (cat && CATEGORIES.includes(cat)) {
      setSelectedCategory(cat);
    }
  }, [searchParams]);

  const [allResearch, setAllResearch] = useState([]);

  const loadResearch = () => {
    apiGetAllResearch({ status: 'published' }).then((res) => {
      if (res.ok) setAllResearch(res.data);
    });
  };

  useEffect(() => { loadResearch(); }, []);

  const handleDeletePaper = async () => {
    if (!deleteTarget) return;
    await apiDeleteResearch(deleteTarget);
    setDeleteTarget(null);
    loadResearch();
  };

  const filtered = useMemo(() => {
    let results = [...allResearch];

    // Search filter
    if (query.trim()) {
      const q = query.toLowerCase();
      results = results.filter(
        (r) =>
          r.title.toLowerCase().includes(q) ||
          r.abstract.toLowerCase().includes(q) ||
          r.authors.some((a) => a.toLowerCase().includes(q)) ||
          r.tags.some((t) => t.toLowerCase().includes(q))
      );
    }

    // Category filter
    if (selectedCategory) {
      results = results.filter((r) => r.category === selectedCategory);
    }

    // Date filter
    if (dateFilter !== 'any') {
      const cutoff = new Date();
      if (dateFilter === '2026') cutoff.setFullYear(2026, 0, 1);
      else if (dateFilter === '2025') cutoff.setFullYear(2025, 0, 1);
      results = results.filter((r) => r.publishedDate && new Date(r.publishedDate) >= cutoff);
    }

    // Sort
    if (sortBy === 'date') {
      results.sort((a, b) => new Date(b.publishedDate) - new Date(a.publishedDate));
    } else {
      results.sort((a, b) => b.citedBy - a.citedBy);
    }

    return results;
  }, [allResearch, query, selectedCategory, sortBy, dateFilter]);

  return (
    <GoogleSignInGate>
      <div className="research-page">
        <div className="research-search-standalone">
          <div className="research-search-bar">
            <FiSearch className="research-search-icon" />
            <input
              type="text"
              placeholder="Search papers, authors, topics…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="research-search-input"
            />
          </div>
        </div>

        <div className="research-body">
          {/* Sidebar filters */}
          <aside className="research-sidebar">
            <div className="sidebar-section">
              <h4>Date</h4>
              <label className={dateFilter === 'any' ? 'active' : ''}>
                <input type="radio" name="date" checked={dateFilter === 'any'} onChange={() => setDateFilter('any')} />
                Any time
              </label>
              <label className={dateFilter === '2026' ? 'active' : ''}>
                <input type="radio" name="date" checked={dateFilter === '2026'} onChange={() => setDateFilter('2026')} />
                Since 2026
              </label>
              <label className={dateFilter === '2025' ? 'active' : ''}>
                <input type="radio" name="date" checked={dateFilter === '2025'} onChange={() => setDateFilter('2025')} />
                Since 2025
              </label>
            </div>

            <div className="sidebar-section">
              <h4>Sort by</h4>
              <label className={sortBy === 'relevance' ? 'active' : ''}>
                <input type="radio" name="sort" checked={sortBy === 'relevance'} onChange={() => setSortBy('relevance')} />
                Sort by relevance
              </label>
              <label className={sortBy === 'date' ? 'active' : ''}>
                <input type="radio" name="sort" checked={sortBy === 'date'} onChange={() => setSortBy('date')} />
                Sort by date
              </label>
            </div>

            <div className="sidebar-section">
              <h4>Category</h4>
              <label className={selectedCategory === '' ? 'active' : ''}>
                <input type="radio" name="cat" checked={selectedCategory === ''} onChange={() => setSelectedCategory('')} />
                All categories
              </label>
              {CATEGORIES.map((cat) => (
                <label key={cat} className={selectedCategory === cat ? 'active' : ''}>
                  <input type="radio" name="cat" checked={selectedCategory === cat} onChange={() => setSelectedCategory(cat)} />
                  {cat}
                </label>
              ))}
            </div>
          </aside>

          {/* Results */}
          <main className="research-results">
            {isAdmin && (
              <div className="inline-admin-bar">
                <span className="inline-admin-badge"><FiSettings /> Admin Mode</span>
                <Link to="/admin/new" className="inline-admin-btn primary">
                  <FiPlus /> New Publication
                </Link>
                <Link to="/admin" className="inline-admin-btn secondary">
                  <FiSettings /> Dashboard
                </Link>
              </div>
            )}

            <div className="results-meta">
              <span>About {filtered.length} results</span>
            </div>

            {filtered.length === 0 ? (
              <div className="no-results">
                <FiBookOpen size={40} />
                <p>No publications found matching your criteria.</p>
              </div>
            ) : (
              filtered.map((paper) => (
                <article key={paper.id} className="paper-card">
                  <div className="paper-title-row">
                    <Link to={`/research/${paper.id}`} className="paper-title">
                      {paper.title}
                    </Link>
                    {paper.pdfUrl && <span className="paper-pdf-badge">[PDF] FVL Reports</span>}
                    {isAdmin && (
                      <div className="paper-admin-actions">
                        <Link to={`/admin/edit/${paper.id}`} className="paper-admin-btn edit" title="Edit">
                          <FiEdit2 />
                        </Link>
                        <button className="paper-admin-btn delete" onClick={() => setDeleteTarget(paper.id)} title="Delete">
                          <FiTrash2 />
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="paper-authors">
                    {paper.authors.join(', ')} — {paper.venue || 'Vector Labs Reports'}
                    {paper.publishedDate && `, ${new Date(paper.publishedDate).getFullYear()}`}
                  </div>
                  <p className="paper-abstract">{paper.abstract.slice(0, 220)}…</p>
                  <div className="paper-actions">
                    <span className="paper-action"><FiStar /> Save</span>
                    <span className="paper-action">❝ Cite</span>
                    <span className="paper-action">Cited by {paper.citedBy}</span>
                    <span className="paper-action">Related articles</span>
                  </div>
                </article>
              ))
            )}

            <ConfirmModal
              open={!!deleteTarget}
              title="Delete Publication"
              message="This will permanently delete this publication.\nThis action cannot be undone."
              confirmLabel="Delete"
              variant="danger"
              onConfirm={handleDeletePaper}
              onCancel={() => setDeleteTarget(null)}
            />
          </main>
        </div>
      </div>
    </GoogleSignInGate>
  );
}
